import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import banner from "../otherImages/sigupbanner.png";
import Logo from "../otherImages/logo-icon.svg";
import { Formik, Form, Field } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  login,
  requestPasswordReset,
  validateResetCode,
  resetPassword,
} from "../redux/slices/authSlice";
import { getUserProfile } from "../redux/slices/userSlice";

const SignInLayer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const timerRef = useRef(null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  useEffect(() => {
    if (resetStep === 2 && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(timerRef.current);
      setResetError("Code has expired. Please request a new one.");
    }

    return () => clearInterval(timerRef.current);
  }, [resetStep, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const formatValidationErrors = (errorData) => {
    if (!errorData) return "";

    if (typeof errorData === "string") return errorData;

    if (errorData.error && typeof errorData.error === "object") {
      const errors = {};
      Object.entries(errorData.error).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          errors[field] = messages.join(", ");
        } else {
          errors[field] = messages;
        }
      });
      return errors;
    }

    return { general: JSON.stringify(errorData) };
  };

  const handleLogin = async (values) => {
    try {
      const result = await dispatch(login(values)).unwrap();
      if (result.success) {
        const userRole = result.data.user.role;
        await dispatch(getUserProfile(true)).unwrap();

        if (userRole === "admin") {
          navigate("/dashboard");
        } else if (userRole === "team") {
          navigate("/client-dashboard");
        } else if (userRole === "customer") {
          navigate("/customer-dashboard");
        }
      }
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  const handleRequestReset = async (values) => {
    setResetLoading(true);
    setResetError("");
    setFieldErrors({});
    try {
      const result = await dispatch(
        requestPasswordReset({ email: values.email }),
      ).unwrap();
      if (result.success) {
        setResetEmail(values.email);
        setResetStep(2);
        setTimeLeft(120);
      } else {
        if (result.data?.error) {
          const formattedErrors = formatValidationErrors(result.data);
          if (typeof formattedErrors === "object") {
            setFieldErrors(formattedErrors);
            setResetError("Please check the form for errors.");
          } else {
            setResetError(formattedErrors);
          }
        } else {
          setResetError(result.message || "Failed to send reset code");
        }
      }
    } catch (err) {
      if (err.data?.error) {
        const formattedErrors = formatValidationErrors(err.data);
        if (typeof formattedErrors === "object") {
          setFieldErrors(formattedErrors);
          setResetError("Please check the form for errors.");
        } else {
          setResetError(formattedErrors);
        }
      } else {
        setResetError(err.message || "Something went wrong");
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleValidateCode = async (values) => {
    setResetLoading(true);
    setResetError("");
    setFieldErrors({});
    try {
      const result = await dispatch(
        validateResetCode({
          email: resetEmail,
          code: values.code,
        }),
      ).unwrap();
      if (result.success) {
        setResetCode(values.code);
        setResetStep(3);
        clearInterval(timerRef.current);
      } else {
        if (result.data?.error) {
          const formattedErrors = formatValidationErrors(result.data);
          if (typeof formattedErrors === "object") {
            setFieldErrors(formattedErrors);
            setResetError("Please check the form for errors.");
          } else {
            setResetError(formattedErrors);
          }
        } else {
          setResetError(result.message || "Invalid code");
        }
      }
    } catch (err) {
      if (err.data?.error) {
        const formattedErrors = formatValidationErrors(err.data);
        if (typeof formattedErrors === "object") {
          setFieldErrors(formattedErrors);
          setResetError("Please check the form for errors.");
        } else {
          setResetError(formattedErrors);
        }
      } else {
        setResetError(err.message || "Code validation failed");
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (values) => {
    setResetLoading(true);
    setResetError("");
    setFieldErrors({});
    try {
      const result = await dispatch(
        resetPassword({
          email: values.email,
          code: values.code,
          password: values.password,
          password_confirmation: values.password_confirmation,
        }),
      ).unwrap();

      if (result.success) {
        showToast(
          "Password reset successful! You can now login with your new password.",
          "success",
        );
        closeResetModal();
      } else {
        if (result.data?.error) {
          const formattedErrors = formatValidationErrors(result.data);
          if (typeof formattedErrors === "object") {
            setFieldErrors(formattedErrors);
            setResetError("Please check the form for errors.");
          } else {
            setResetError(formattedErrors);
          }
        } else {
          setResetError(result.message || "Failed to reset password");
        }
      }
    } catch (err) {
      if (err.data?.error) {
        const formattedErrors = formatValidationErrors(err.data);
        if (typeof formattedErrors === "object") {
          setFieldErrors(formattedErrors);
          setResetError("Please check the form for errors.");
        } else {
          setResetError(formattedErrors);
        }
      } else {
        setResetError(err.message || "Password reset failed");
      }
      console.error("Password reset failed:", err);
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetModal = () => {
    setShowForgotPassword(false);
    setResetStep(1);
    setResetEmail("");
    setResetCode("");
    setResetError("");
    setFieldErrors({});
    setTimeLeft(120);
    clearInterval(timerRef.current);
  };

  const resendCode = () => {
    if (resetEmail) {
      handleRequestReset({ email: resetEmail });
      setTimeLeft(120);
      showToast("New code sent to your email!", "success");
    }
  };

  // Error Message Component
  const ErrorMessage = ({ message, type = "error" }) => {
    if (!message) return null;

    const icons = {
      error: "❌",
      warning: "⚠️",
      info: "ℹ️",
    };

    return (
      <div className={`error-message error-message-${type}`}>
        <span className="error-icon">{icons[type] || icons.error}</span>
        <span className="error-text">{message}</span>
      </div>
    );
  };

  // Field Error Component
  const FieldError = ({ error }) => {
    if (!error) return null;
    return (
      <div className="field-error">
        <Icon icon="mdi:alert-circle" width={14} height={14} />
        <span>{error}</span>
      </div>
    );
  };

  return (
    <section className="auth bg-base d-flex authSection">
      {/* Toast Notification with better styling */}
      {toast.show && (
        <div className={`toast-notification toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-icon">
              {toast.type === "success" ? "✅" : "❌"}
            </span>
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: "", type: "" })}>
              ×
            </button>
          </div>
        </div>
      )}

      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img
            className="h-100vh w-100"
            src={banner}
            alt=""
            style={{ objectFit: "cover", objectPosition: "bottom" }}
          />
        </div>
      </div>

      <div className="auth-right py-22 px-24 d-flex flex-column justify-content-center">
        <div className="text-center authImage">
          <Link to="/" className="mb-40 max-w-290-px">
            <img src={Logo} alt="" />
          </Link>
        </div>

        <div className="w-100 rightCol">
          <div className="text-center">
            <h4 className="mb-5 fw-bold fs-1 text-center">
              Login to Your Account
            </h4>
          </div>

          {/* Global Error Display for Login */}
          {error && (
            <div className="global-error mb-4">
              <div className="global-error-content">
                <Icon icon="mdi:alert-circle" width={20} height={20} />
                <span>{error}</span>
              </div>
            </div>
          )}

          <Formik
            initialValues={{ email: "", password: "" }}
            onSubmit={handleLogin}>
            {({ errors, touched }) => (
              <Form>
                <div className="mb-16">
                  <label className="authLabel fw-bold mb-10">
                    Email Address
                  </label>
                  <div
                    className={`icon-field ${touched.email && errors.email ? "has-error" : ""}`}>
                    <span className="icon top-50 translate-middle-y">
                      <Icon icon="mage:email" />
                    </span>
                    <Field
                      type="email"
                      name="email"
                      className="form-control h-56-px bg-neutral-50 radius-12"
                      placeholder="Email"
                      autoComplete="email"
                    />
                  </div>
                  {touched.email && errors.email && (
                    <FieldError error={errors.email} />
                  )}
                </div>

                <div className="position-relative mb-20">
                  <label className="authLabel fw-bold mb-10">Password</label>
                  <div
                    className={`icon-field ${touched.password && errors.password ? "has-error" : ""}`}>
                    <span className="icon top-50 translate-middle-y">
                      <Icon icon="solar:lock-password-outline" />
                    </span>
                    <Field
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="form-control h-56-px bg-neutral-50 radius-12"
                      placeholder="Password"
                      autoComplete="current-password"
                    />
                    <span
                      className={`toggle-password cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light ri-${
                        showPassword ? "eye-off-line" : "eye-line"
                      }`}
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </div>
                  {touched.password && errors.password && (
                    <FieldError error={errors.password} />
                  )}
                </div>

                <div className="d-flex justify-content-between gap-2">
                  <div className="form-check style-check d-flex align-items-center">
                    <input
                      className="form-check-input border border-neutral-300"
                      type="checkbox"
                      id="remember"
                    />
                    <label className="form-check-label" htmlFor="remember">
                      Remember me
                    </label>
                  </div>
                  <Link
                    to="#"
                    className="primaryColor fw-medium"
                    onClick={() => setShowForgotPassword(true)}>
                    Forgot Password?
                  </Link>
                </div>

                <button
                  type="submit"
                  className="btn bg-primary py-16 w-100 radius-12 mt-32"
                  disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"></span>
                      Logging in...
                    </>
                  ) : (
                    "Log In"
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Forgot Password Modal with improved error styling */}
      {showForgotPassword && (
        <>
          <div className="modal-overlay" onClick={closeResetModal} />
          <div className="modal-content">
            <div className="modal-header">
              <h5>
                {resetStep === 1 && "Reset Password"}
                {resetStep === 2 && "Enter Reset Code"}
                {resetStep === 3 && "New Password"}
              </h5>
              <button onClick={closeResetModal} className="modal-close-btn">
                ×
              </button>
            </div>

            <div className="modal-body">
              {/* General Error Display */}
              {resetError && <ErrorMessage message={resetError} type="error" />}

              {resetStep === 1 && (
                <>
                  <p className="modal-description">
                    Enter your email to receive a reset code
                  </p>
                  <Formik
                    initialValues={{ email: "" }}
                    onSubmit={handleRequestReset}>
                    {({ errors, touched }) => (
                      <Form>
                        <div className="form-group mb-3">
                          <Field
                            type="email"
                            name="email"
                            placeholder="Enter your email"
                            className={`form-control ${touched.email && (errors.email || fieldErrors.email) ? "is-invalid" : ""}`}
                            required
                          />
                          {touched.email && errors.email && (
                            <FieldError error={errors.email} />
                          )}
                          {fieldErrors.email && (
                            <FieldError error={fieldErrors.email} />
                          )}
                        </div>
                        <button
                          type="submit"
                          className="btn bg-primary btn-primary w-100"
                          disabled={resetLoading}>
                          {resetLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Sending...
                            </>
                          ) : (
                            "Send Reset Code"
                          )}
                        </button>
                      </Form>
                    )}
                  </Formik>
                </>
              )}

              {resetStep === 2 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="mb-0">
                      Enter the 6-digit code sent to{" "}
                      <strong>{resetEmail}</strong>
                    </p>
                    <div
                      className={`code-timer ${
                        timeLeft > 30
                          ? "timer-success"
                          : timeLeft > 10
                            ? "timer-warning"
                            : "timer-danger"
                      }`}>
                      ⏱️ {formatTime(timeLeft)}
                    </div>
                  </div>
                  <Formik
                    initialValues={{ code: "" }}
                    onSubmit={handleValidateCode}>
                    {({ errors, touched }) => (
                      <Form>
                        <div className="form-group mb-3">
                          <Field
                            type="text"
                            name="code"
                            placeholder="Enter 6-digit code"
                            className={`form-control text-center letter-spacing-2 ${touched.code && (errors.code || fieldErrors.code) ? "is-invalid" : ""}`}
                            maxLength="6"
                            required
                          />
                          {touched.code && errors.code && (
                            <FieldError error={errors.code} />
                          )}
                          {fieldErrors.code && (
                            <FieldError error={fieldErrors.code} />
                          )}
                        </div>
                        <button
                          type="submit"
                          className="btn bg-primary btn-primary w-100 mb-2"
                          disabled={resetLoading || timeLeft === 0}>
                          {resetLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Verifying...
                            </>
                          ) : (
                            "Verify Code"
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-100"
                          onClick={resendCode}
                          disabled={resetLoading}>
                          Resend Code
                        </button>
                      </Form>
                    )}
                  </Formik>
                </>
              )}

              {resetStep === 3 && (
                <>
                  <p className="modal-description">
                    Create a new password for your account
                  </p>
                  <Formik
                    initialValues={{
                      email: resetEmail,
                      code: resetCode,
                      password: "",
                      password_confirmation: "",
                    }}
                    onSubmit={handleResetPassword}>
                    {({ values, errors, touched }) => (
                      <Form>
                        <div className="form-group mb-3">
                          <label className="form-label fw-semibold mb-2">
                            Email
                          </label>
                          <Field
                            type="email"
                            name="email"
                            className="form-control bg-light"
                            required
                            readOnly
                          />
                        </div>

                        <div className="form-group mb-3">
                          <label className="form-label fw-semibold mb-2">
                            Reset Code
                          </label>
                          <Field
                            type="text"
                            name="code"
                            className={`form-control ${touched.code && (errors.code || fieldErrors.code) ? "is-invalid" : ""}`}
                            maxLength="6"
                            required
                          />
                          {touched.code && errors.code && (
                            <FieldError error={errors.code} />
                          )}
                          {fieldErrors.code && (
                            <FieldError error={fieldErrors.code} />
                          )}
                        </div>

                        <div className="form-group mb-3">
                          <label className="form-label fw-semibold mb-2">
                            New Password *
                          </label>
                          <Field
                            type="password"
                            name="password"
                            placeholder="Minimum 8 characters"
                            className={`form-control ${touched.password && (errors.password || fieldErrors.password) ? "is-invalid" : ""}`}
                            required
                          />
                          {touched.password && errors.password && (
                            <FieldError error={errors.password} />
                          )}
                          {fieldErrors.password && (
                            <FieldError error={fieldErrors.password} />
                          )}
                          {values.password && values.password.length < 8 && (
                            <div className="password-hint">
                              <Icon
                                icon="mdi:information"
                                width={12}
                                height={12}
                              />
                              Password must be at least 8 characters
                            </div>
                          )}
                        </div>

                        <div className="form-group mb-3">
                          <label className="form-label fw-semibold mb-2">
                            Confirm New Password *
                          </label>
                          <Field
                            type="password"
                            name="password_confirmation"
                            placeholder="Confirm new password"
                            className={`form-control ${touched.password_confirmation && (errors.password_confirmation || fieldErrors.password_confirmation) ? "is-invalid" : ""}`}
                            required
                          />
                          {touched.password_confirmation &&
                            errors.password_confirmation && (
                              <FieldError
                                error={errors.password_confirmation}
                              />
                            )}
                          {fieldErrors.password_confirmation && (
                            <FieldError
                              error={fieldErrors.password_confirmation}
                            />
                          )}
                          {values.password &&
                            values.password_confirmation &&
                            values.password !==
                              values.password_confirmation && (
                              <div className="password-hint error">
                                <Icon icon="mdi:alert" width={12} height={12} />
                                Passwords don't match
                              </div>
                            )}
                        </div>

                        <button
                          type="submit"
                          className="btn bg-primary btn-primary w-100"
                          disabled={resetLoading || values.password.length < 8}>
                          {resetLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Resetting...
                            </>
                          ) : (
                            "Reset Password"
                          )}
                        </button>
                      </Form>
                    )}
                  </Formik>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <style jsx>{`
        /* Global Error Styles */
        .global-error {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          border-left: 4px solid #dc2626;
          border-radius: 8px;
          padding: 12px 16px;
          animation: slideDown 0.3s ease-out;
        }

        .global-error-content {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #991b1b;
          font-size: 14px;
          font-weight: 500;
        }

        /* Error Message Component */
        .error-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          animation: slideDown 0.3s ease-out;
        }

        .error-message-error {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          border-left: 4px solid #dc2626;
          color: #991b1b;
        }

        .error-message-warning {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-left: 4px solid #f59e0b;
          color: #92400e;
        }

        .error-message-info {
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          border-left: 4px solid #3b82f6;
          color: #1e40af;
        }

        .error-icon {
          font-size: 18px;
        }

        .error-text {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
        }

        /* Field Error Styles */
        .field-error {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 6px;
          color: #dc2626;
          font-size: 12px;
          animation: fadeIn 0.2s ease-out;
        }

        .field-error svg {
          flex-shrink: 0;
        }

        /* Input Error State */
        .has-error .form-control,
        .form-control.is-invalid {
          border-color: #dc2626 !important;
          background-color: #fef2f2 !important;
          box-shadow: 0 0 0 1px #dc2626 !important;
        }

        /* Password Hint */
        .password-hint {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 6px;
          font-size: 12px;
          color: #6b7280;
        }

        .password-hint.error {
          color: #dc2626;
        }

        /* Timer Styles */
        .code-timer {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
          font-family: monospace;
        }

        .timer-success {
          background: #d1fae5;
          color: #065f46;
        }

        .timer-warning {
          background: #fed7aa;
          color: #92400e;
        }

        .timer-danger {
          background: #fee2e2;
          color: #991b1b;
          animation: pulse 1s infinite;
        }

        /* Modal Styles Enhancement */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 1040;
          animation: fadeIn 0.3s ease-out;
        }

        .modal-content {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 450px;
          max-height: 90vh;
          overflow-y: auto;
          z-index: 1050;
          animation: modalSlideIn 0.3s ease-out;
          box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 16px 16px 0 0;
        }

        .modal-header h5 {
          margin: 0;
          color: white;
          font-weight: 600;
          font-size: 18px;
        }

        .modal-close-btn {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.05);
        }

        .modal-body {
          padding: 24px;
        }

        .modal-description {
          color: #6b7280;
          margin-bottom: 20px;
          font-size: 14px;
        }

        /* Animations */
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translate(-50%, -48%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        /* Toast Notification Enhancement */
        .toast-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 1100;
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .toast-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .toast-error {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }

        .toast-content {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 20px;
          border-radius: 12px;
          color: white;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        /* Letter spacing for code input */
        .letter-spacing-2 {
          letter-spacing: 4px;
          font-size: 20px;
          font-weight: 600;
        }

        /* Spinner animation */
        .spinner-border {
          vertical-align: middle;
        }
      `}</style>
    </section>
  );
};

export default SignInLayer;
